// ---------------------------------------------------------------------------
// Flight-tracking utilities — shared between WarRoomModal and useFlightTracking
// ---------------------------------------------------------------------------

export const IATA_TO_ICAO = {
  UA: "UAL", LH: "DLH", AC: "ACA", NH: "ANA", OZ: "AAR",
  SK: "SAS", TK: "THY", OS: "AUA", LO: "LOT", CA: "CCA",
  JP: "ADR", ZH: "CSZ", SN: "BEL", OU: "CTN", MS: "MSR",
  SQ: "SIA", TG: "THA", AY: "FIN", LX: "SWR", ET: "ETH",
  SA: "SAA", EG: "EGF", BR: "EVA", EY: "EYU", NZ: "ANZ",
  AZ: "ITY", AI: "AIC", EK: "UAE", QF: "QFA", CX: "CPA",
  MH: "MAS", TN: "THT", GF: "GFA", WY: "OMA", KE: "KAL",
  CI: "CAL", MU: "CES", HU: "CHH",
  CM: "CMP", TP: "TAP", A3: "AEE",
  AA: "AAL", DL: "DAL", AS: "ASA", B6: "JBU", WN: "SWA",
};

export const PROVIDERS = [
  // adsb.lol / adsb.fi / airplanes.live all block browser CORS — routed through
  // a Supabase Edge Function proxy that calls them server-side. Primary because
  // it has worldwide coverage and no per-user quota.
  { key: "proxy",   label: "ADS-B"  },
  // Anonymous OpenSky: ~100 calls/day per IP, and the bbox below only covers
  // the western hemisphere, so it's a fallback only.
  { key: "opensky", label: "OpenSky" },
];

// How long an aircraft stays on the map after it drops out of a poll result.
// Smooths over single missed polls and patchy receiver coverage.
export const AIRCRAFT_RETAIN_MS = 3 * 60 * 1000;

// Merge a fresh poll into the previous aircraft list: fresh vectors win, and
// aircraft missing from this poll are kept until AIRCRAFT_RETAIN_MS has passed.
export function mergeAircraft(prev, fresh, now = Date.now()) {
  const byCs = new Map();
  for (const sv of prev) {
    if (sv.callsign && now - (sv.seenAt ?? 0) < AIRCRAFT_RETAIN_MS) byCs.set(sv.callsign, sv);
  }
  for (const sv of fresh) {
    if (sv.callsign) byCs.set(sv.callsign, { ...sv, seenAt: now });
  }
  return [...byCs.values()];
}

// ICAO type designator (ADS-B `t` field) → short display name.
// The type comes from the feed's aircraft database, not the transponder, and is
// sometimes wrong (an Air Canada A220 once came back as "AS50", a helicopter).
// Every flight here is an airliner, so unlisted codes are hidden rather than shown.
const AIRCRAFT_TYPE_NAMES = {
  A318: "A318", A19N: "A319neo", A20N: "A320neo", A21N: "A321neo",
  A319: "A319", A320: "A320", A321: "A321",
  A332: "A330-200", A333: "A330-300", A338: "A330-800neo", A339: "A330-900neo",
  A343: "A340-300", A346: "A340-600",
  A359: "A350-900", A35K: "A350-1000", A388: "A380",
  BCS1: "A220-100", BCS3: "A220-300",
  B712: "717-200",
  B736: "737-600", B737: "737-700", B738: "737-800", B739: "737-900",
  B37M: "737 MAX 7", B38M: "737 MAX 8", B39M: "737 MAX 9", B3XM: "737 MAX 10",
  B744: "747-400", B748: "747-8",
  B752: "757-200", B753: "757-300",
  B762: "767-200", B763: "767-300", B764: "767-400",
  B772: "777-200", B77L: "777-200LR", B77W: "777-300ER", B778: "777-8", B779: "777-9",
  B788: "787-8", B789: "787-9", B78X: "787-10",
  AT75: "ATR 72-500", AT76: "ATR 72-600", DH8D: "Dash 8-400",
  CRJ2: "CRJ-200", CRJ7: "CRJ-700", CRJ9: "CRJ-900", E145: "ERJ-145",
  E170: "E170", E75L: "E175", E75S: "E175", E190: "E190", E195: "E195",
  E290: "E190-E2", E295: "E195-E2",
};

export function aircraftTypeName(code) {
  if (!code) return null;
  const c = String(code).trim().toUpperCase();
  return AIRCRAFT_TYPE_NAMES[c] ?? null;
}

// Normalised aircraft object: { callsign, hex, lat, lon, altM, speedMs, heading, onGround, vertRateFpm, type }
// `type` is the ICAO type designator (e.g. "B77W"); OpenSky doesn't provide it.

export function normaliseAdsbAc(ac) {
  // Shared schema for adsb.lol, adsb.fi, airplanes.live
  // alt_baro is in feet; gs is in knots
  return {
    callsign: (ac.flight || "").trim().toUpperCase(),
    hex: (ac.hex || "").trim().toLowerCase() || null,
    lat: ac.lat,
    lon: ac.lon,
    altM: typeof ac.alt_baro === "number" ? ac.alt_baro / 3.28084 : null,
    speedMs: ac.gs != null ? ac.gs * 0.514444 : null,
    heading: ac.track ?? 0,
    onGround: !!ac.ground,
    vertRateFpm: ac.baro_rate ?? null,
    type: ac.t || null,
  };
}

export async function fetchOpenSky(callsigns, signal) {
  const res = await fetch(
    "https://opensky-network.org/api/states/all?lamin=0&lomin=-180&lamax=72&lomax=-55",
    { signal }
  );
  if (res.status === 429) { const e = new Error("429"); e.rateLimited = true; throw e; }
  if (!res.ok) throw new Error("HTTP " + res.status);
  const data = await res.json();
  const csSet = new Set(callsigns);
  return (data.states || [])
    .filter(sv => sv[1] && csSet.has(sv[1].trim().toUpperCase()) && sv[5] !== null && sv[6] !== null)
    .map(sv => ({
      callsign: sv[1].trim().toUpperCase(),
      lat: sv[6], lon: sv[5],
      altM: sv[7],
      speedMs: sv[9],
      heading: sv[10] ?? 0,
      onGround: !!sv[8],
      vertRateFpm: sv[11] != null ? sv[11] * 196.85 : null, // m/s → ft/min
    }));
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

export async function fetchFromProxy(callsigns, signal, icao24s = []) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/adsb-proxy`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${SUPABASE_KEY}`,
    },
    body: JSON.stringify({ callsigns, icao24s }),
    signal,
  });
  if (!res.ok) throw new Error("Proxy HTTP " + res.status);
  const data = await res.json();
  if (data.error) throw new Error("Proxy error: " + data.error);
  return (data.ac || [])
    .filter(ac => ac.lat != null && ac.lon != null)
    .map(normaliseAdsbAc);
}

// LAX operating conditions (FAA ground stop / delay programs / delays + KLAX METAR) via the
// lax-conditions Edge Function, which caches for 5 min.
export async function fetchLaxConditions(signal) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/lax-conditions`, {
    headers: { "apikey": SUPABASE_KEY, "Authorization": `Bearer ${SUPABASE_KEY}` },
    signal,
  });
  if (!res.ok) throw new Error("LAX conditions HTTP " + res.status);
  return res.json();
}

export async function lookupCallsign(flightIata, signal) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/flight-callsign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": SUPABASE_KEY,
      "Authorization": `Bearer ${SUPABASE_KEY}`,
    },
    body: JSON.stringify({ flight_iata: flightIata }),
    signal,
  });
  if (!res.ok) throw new Error("Callsign lookup HTTP " + res.status);
  const data = await res.json();
  if (data.error) throw new Error("Callsign lookup error: " + data.error);
  return data; // { icao24, registration, callsign }
}

export async function fetchFromProvider(key, callsigns, signal) {
  switch (key) {
    case "opensky": return fetchOpenSky(callsigns, signal);
    case "proxy":   return fetchFromProxy(callsigns, signal);
    default: throw new Error("Unknown provider: " + key);
  }
}

export function flightToIcaoCallsign(flt) {
  if (!flt) return null;
  const m = flt.match(/^([A-Z]{2})(\d+)(.*)$/i);
  if (!m) return null;
  const iata = m[1].toUpperCase();
  const num  = m[2];
  const suffix = m[3];
  const icao = IATA_TO_ICAO[iata];
  if (!icao) return null;
  return icao + num + suffix;
}

// Some airlines broadcast zero-padded callsigns (e.g. EVA012 instead of EVA12).
// Returns all shorter-to-longer padded variants up to 3 digits (e.g. EVA2 → ["EVA02","EVA002"]).
export function paddedCallsigns(cs) {
  if (!cs) return [];
  const m = cs.match(/^([A-Z]+)(\d+)(.*)$/);
  if (!m) return [];
  const prefix = m[1], num = m[2], suffix = m[3];
  const variants = [];
  for (let len = num.length + 1; len <= 3; len++) {
    variants.push(prefix + num.padStart(len, "0") + suffix);
  }
  return variants;
}

// Flights flown by a group/regional operator broadcast that operator's callsign, not the
// marketing carrier's, so they also need these variants:
// - UA 3000+ is often SkyWest (United Express): "SKW####" rather than "UAL####"
// - AC 1000–1999 is Air Canada Rouge: "ROU####" rather than "ACA####" (e.g. AC1742 → ROU1742)
// - AC 8000–8999 is Air Canada Express by Jazz: "JZA####"
export function regionalCallsigns(iataFlt) {
  if (!iataFlt) return [];
  const m = iataFlt.match(/^(UA|AC)(\d{4})$/i);
  if (!m) return [];
  const carrier = m[1].toUpperCase(), num = m[2], n = parseInt(num, 10);
  if (carrier === "UA") return n >= 3000 ? [`SKW${num}`] : [];
  if (n >= 1000 && n <= 1999) return [`ROU${num}`];
  if (n >= 8000 && n <= 8999) return [`JZA${num}`];
  return [];
}

// Derive a display-ready flight status from a normalised aircraft vector.
// Returns "FINAL_DESCENT", "LANDED", "TAKING_OFF", or null (no badge needed).
export function deriveFlightStatus(sv) {
  if (!sv) return null;
  const altFt = sv.altM != null ? Math.round(sv.altM * 3.28084 / 100) * 100 : null;
  const speedKts = sv.speedMs != null ? sv.speedMs * 1.94384 : null;
  const isOnGround = sv.onGround || altFt === null || altFt <= 50;
  if (isOnGround) {
    // Any ground movement > 3 kts = departed the gate (pushback/taxi counts)
    return (speedKts != null && speedKts > 3) ? "TAKING_OFF" : "LANDED";
  }
  const vr = sv.vertRateFpm;
  if (vr != null && vr < -200 && altFt != null && altFt < 10000) return "FINAL_DESCENT";
  return null; // cruising/climbing/descending — no badge needed
}

// ---------------------------------------------------------------------------
// Situation alerts — holding, go-around, diversion — from a short ADS-B track.
// Best effort: positions arrive every ~60 s and coverage is patchy, so each rule
// needs several consistent points before it fires.
// ---------------------------------------------------------------------------

export const SITUATION_LABELS = { HOLDING: "Holding", GO_AROUND: "Go-around", DIVERTING: "Diverting" };

const TRACK_MAX_AGE_MS = 20 * 60 * 1000;

function kmBetween(lat1, lon1, lat2, lon2) {
  const R = 6371, toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Append a fresh vector to a callsign's track, keeping the last 20 minutes
export function recordTrackPoint(track, sv, now = Date.now()) {
  const point = {
    t: now, lat: sv.lat, lon: sv.lon, heading: sv.heading ?? null,
    altFt: sv.altM != null ? sv.altM * 3.28084 : null,
    vr: sv.vertRateFpm ?? null, onGround: !!sv.onGround,
  };
  return [...(track || []), point].filter(p => now - p.t <= TRACK_MAX_AGE_MS);
}

// "HOLDING" | "GO_AROUND" | "DIVERTING" | null for a track (oldest → newest) toward `dest` [lat, lon]
export function detectSituation(track, dest) {
  if (!track || track.length < 2) return null;
  const last = track[track.length - 1];
  if (last.onGround) return null;
  const dist = p => kmBetween(p.lat, p.lon, dest[0], dest[1]);

  // Go-around: got below 3,000 ft near LAX, then climbed more than 1,000 ft above that low point.
  // Kept to within ~20 km of LAX so a climb-out from the origin airport never counts.
  if (dist(last) < 20 && last.altFt != null) {
    const lows = track.filter(p => p !== last && last.t - p.t <= 6 * 60000 && !p.onGround
      && p.altFt != null && p.altFt < 3000 && dist(p) < 20);
    if (lows.length && last.altFt - Math.min(...lows.map(p => p.altFt)) > 1000) return "GO_AROUND";
  }

  // Holding: turning circles near the destination without getting closer or changing level
  const recent = track.filter(p => last.t - p.t <= 12 * 60000 && !p.onGround);
  if (recent.length >= 4 && recent.every(p => dist(p) < 150 && p.heading != null)) {
    let turned = 0;
    for (let i = 1; i < recent.length; i++) {
      const d = ((recent[i].heading - recent[i - 1].heading + 540) % 360) - 180;
      turned += Math.abs(d);
    }
    const closer = dist(recent[0]) - dist(last);
    const alts = recent.map(p => p.altFt).filter(a => a != null);
    const altSpan = alts.length ? Math.max(...alts) - Math.min(...alts) : 0;
    if (turned >= 300 && closer < 15 && altSpan <= 3000) return "HOLDING";
  }

  // Diverting: well away from the destination, descending, and moving further away each poll
  const tail = track.slice(-3);
  if (tail.length === 3 && dist(last) > 60 && last.altFt != null && last.altFt < 15000) {
    const away = dist(tail[1]) - dist(tail[0]) >= 3 && dist(tail[2]) - dist(tail[1]) >= 3;
    const descending = (last.vr ?? 0) < -300 || (tail[0].altFt != null && last.altFt < tail[0].altFt - 1000);
    if (away && descending) return "DIVERTING";
  }
  return null;
}
